// JSON encoding that preserves types lost by plain JSON.stringify/parse.
//
// The original architecture used Web Worker postMessage, which uses
// structured clone — Date/Map/Set/etc. all survive. Replacing the Worker
// with WebSocket + JSON silently loses those. This module wraps non-trivial
// values in a typed envelope so they round-trip identically.
//
// Currently handled:
//   - Date  → { __bbgm_date__: <ISO string> }
//   - Set   → { __bbgm_set__: <array of values> }
//   - Map   → { __bbgm_map__: <array of [key, value] pairs> }
//
// The envelope keys are unique-looking strings to minimize collision risk
// with real data fields.
//
// Usage:
//   - server WS send + SQLite save:   stringify(value)
//   - UI WS receive + SQLite restore: parse(jsonString)

const DATE_MARKER = "__bbgm_date__";
const SET_MARKER = "__bbgm_set__";
const MAP_MARKER = "__bbgm_map__";

// Regular function (not arrow) so JSON.stringify binds `this` to the parent
// object. Date.prototype.toJSON converts the value to an ISO string BEFORE
// replacer runs, so we recover the original via `this[key]`.
function replacer(this: any, key: string, value: unknown): unknown {
	const original = this?.[key];
	if (original instanceof Date) {
		return { [DATE_MARKER]: original.toISOString() };
	}
	if (original instanceof Set) {
		return { [SET_MARKER]: [...original] };
	}
	if (original instanceof Map) {
		return { [MAP_MARKER]: [...original] };
	}
	return value;
}

function reviver(_key: string, value: unknown): unknown {
	if (value !== null && typeof value === "object") {
		const v = value as any;
		if (typeof v[DATE_MARKER] === "string") {
			return new Date(v[DATE_MARKER]);
		}
		if (Array.isArray(v[SET_MARKER])) {
			return new Set(v[SET_MARKER]);
		}
		if (Array.isArray(v[MAP_MARKER])) {
			return new Map(v[MAP_MARKER]);
		}
	}
	return value;
}

export const stringify = (value: unknown): string =>
	JSON.stringify(value, replacer);

export const parse = (json: string): unknown => JSON.parse(json, reviver);
